import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

/**
 * Renders text clamped to `maxLines` (default 3) with an auto ellipsis. When the
 * text overflows, a small "Show more / less" text button toggles the clamp.
 * Hard newlines are preserved (whiteSpace: pre-wrap) so they count as lines.
 */
function ExpandableText({ text, maxLines = 3, sx }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    // Measure against the clamped box: if content is taller than the visible
    // area, the toggle is needed. Re-run whenever the text changes.
    if (!expanded) {
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }
  }, [text, expanded, maxLines]);

  if (!text) return null;

  const clampStyles = expanded
    ? {}
    : {
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  const handleToggle = (event) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <Box sx={sx}>
      <Typography
        ref={textRef}
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", ...clampStyles }}
      >
        {text}
      </Typography>

      {(isClamped || expanded) && (
        <Typography
          component="button"
          type="button"
          variant="caption"
          onClick={handleToggle}
          sx={{
            mt: 0.25,
            p: 0,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          {expanded ? t("common.showLess") : t("common.showMore")}
        </Typography>
      )}
    </Box>
  );
}

export default ExpandableText;
